/**
 * Browser-local persistence for support-bot UI state that isn't (yet)
 * stored on the conversation rows themselves:
 *   - Hidden conversation ids (declutter the sidebar without deleting)
 *   - Applied proposal fingerprints (so "Apply change" stays Applied
 *     across refresh in this browser)
 *   - Deep mode (composer toggle; survives refresh and conversation switches)
 *
 * Keys are namespaced and JSON-encoded. Failures (private browsing
 * quota, SSR) are treated as empty state so the chat still works.
 */

import type { ProposeChangeExecutePayload } from './propose-change'

const HIDDEN_CONVERSATIONS_KEY = 'chckup.supportBot.hiddenConversationIds'
const APPLIED_PROPOSALS_KEY = 'chckup.supportBot.appliedProposalKeys'
const DEEP_MODE_KEY = 'chckup.supportBot.deepMode'

function readStringSet(storageKey: string): Set<string> {
    if (typeof window === 'undefined') return new Set()
    try {
        const raw = window.localStorage.getItem(storageKey)
        if (!raw) return new Set()
        const parsed: unknown = JSON.parse(raw)
        if (!Array.isArray(parsed)) return new Set()
        return new Set(
            parsed.filter((v): v is string => typeof v === 'string' && v.length > 0),
        )
    } catch {
        return new Set()
    }
}

function writeStringSet(storageKey: string, values: Set<string>): void {
    if (typeof window === 'undefined') return
    try {
        window.localStorage.setItem(
            storageKey,
            JSON.stringify(Array.from(values)),
        )
    } catch {
        // Quota / privacy mode — ignore; UI still works for the session.
    }
}

export function loadHiddenConversationIds(): Set<string> {
    return readStringSet(HIDDEN_CONVERSATIONS_KEY)
}

export function persistHiddenConversationIds(ids: Set<string>): void {
    writeStringSet(HIDDEN_CONVERSATIONS_KEY, ids)
}

export function loadAppliedProposalKeys(): Set<string> {
    return readStringSet(APPLIED_PROPOSALS_KEY)
}

export function persistAppliedProposalKeys(keys: Set<string>): void {
    writeStringSet(APPLIED_PROPOSALS_KEY, keys)
}

export function loadDeepMode(): boolean {
    if (typeof window === 'undefined') return false
    try {
        return window.localStorage.getItem(DEEP_MODE_KEY) === 'true'
    } catch {
        return false
    }
}

export function persistDeepMode(enabled: boolean): void {
    if (typeof window === 'undefined') return
    try {
        window.localStorage.setItem(DEEP_MODE_KEY, enabled ? 'true' : 'false')
    } catch {
        // Quota / privacy mode — toggle still works for the session.
    }
}

/**
 * Stable key for a proposal within a conversation. Same entity+patch in
 * the same chat always maps to the same key so refresh restores Applied.
 */
export function proposalAppliedStorageKey(
    conversationId: string,
    execute: ProposeChangeExecutePayload,
): string {
    const identity =
        execute.operation === 'create'
            ? `create:${execute.clinic_id ?? ''}`
            : execute.target_entity_id ?? ''
    return [
        conversationId,
        execute.operation,
        execute.target_entity_type,
        identity,
        stableStringify(execute.patch),
    ].join('::')
}

/** Deterministic JSON stringify (sorted object keys) for fingerprinting. */
function stableStringify(value: unknown): string {
    return JSON.stringify(sortJson(value))
}

function sortJson(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(sortJson)
    }
    if (value && typeof value === 'object') {
        const entries = Object.entries(value as Record<string, unknown>).sort(
            ([a], [b]) => a.localeCompare(b),
        )
        const out: Record<string, unknown> = {}
        for (const [k, v] of entries) {
            out[k] = sortJson(v)
        }
        return out
    }
    return value
}
